import { Skeleton } from '@/components/atoms';
import { useTranslation, useUIColors } from '@/core/hooks';
import {
  getReservationListingChipColors,
  type ReservationStatus,
} from '@/core/reservation';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { getReservationVisibleCopy } from '../constants/webReservationStatus';
import { ReservationStatusMessage } from './ReservationStatusMessage';
import { ReservationStatusToneIcon } from './ReservationStatusToneIcon';

export interface UnitReservedStickyBannerProps {
  mappedStatus: ReservationStatus | null;
  /** Raw SF status for ADHA Contentful `reservationStatus.<normalized>` lookup. */
  rawStatus?: string | null;
}

const OVERLAY_ICON_SIZE = 19;

/**
 * Mobile details overlay — same layout as before; only the generic
 * Pending / In Progress / Confirmed title is omitted (message stays).
 */
export function UnitReservedStickyBanner({
  mappedStatus,
  rawStatus,
}: Readonly<UnitReservedStickyBannerProps>) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const copy = getReservationVisibleCopy(mappedStatus, t, rawStatus);
  if (!mappedStatus || !copy) {
    return null;
  }
  const listingChip = getReservationListingChipColors(mappedStatus, colors);
  const accessibilityLabel = copy.listingMessage ?? undefined;

  return (
    <View
      style={[
        styles.banner,
        {
          backgroundColor: colors.backgroundLevel1,
          borderColor: listingChip.text,
        },
      ]}
      accessibilityRole="text"
      accessibilityLabel={accessibilityLabel}
    >
      <View
        pointerEvents="none"
        style={[
          StyleSheet.absoluteFillObject,
          { backgroundColor: listingChip.bg, borderRadius: 11 },
        ]}
      />
      {copy.isMessageReady ? (
        <ReservationStatusToneIcon
          mappedStatus={mappedStatus}
          color={listingChip.text}
          size={OVERLAY_ICON_SIZE}
        />
      ) : (
        <Skeleton
          width={OVERLAY_ICON_SIZE}
          height={OVERLAY_ICON_SIZE}
          variant="circular"
          borderRadius={OVERLAY_ICON_SIZE / 2}
        />
      )}
      <View style={styles.textCol}>
        <ReservationStatusMessage
          mappedStatus={mappedStatus}
          rawStatus={rawStatus ?? null}
          color="secondary"
          variant="footnote"
          style={styles.message}
          skeletonWidth={160}
          skeletonHeight={13}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginHorizontal: 16,
    marginTop: 0,
    marginBottom: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 12,
    borderWidth: 1,
    overflow: 'hidden',
  },
  textCol: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  /** Font/Size/2XS Regular, LH 100% */
  message: {
    lineHeight: 13,
  },
});

export default UnitReservedStickyBanner;
